<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    v-if="showConfirmation"
    ref="confirmation"
    class="space-y-4 rounded-xl bg-blue-lighter p-6 lg:rounded-2xl lg:p-9"
  >
    <h2
      v-if="data.confirmation.title"
      class="mb-6 font-sans text-3xl font-bold text-blue lg:text-4xl"
    >
      {{ data.confirmation.title }}
    </h2>
    <div
      class="custom-list prose font-sans text-base leading-[21px] lg:prose-lg prose-a:text-blue prose-a:no-underline hover:prose-a:text-blue-dark lg:text-lg lg:leading-[26px]"
      v-html="data.confirmation.message"
    ></div>
  </div>

  <form
    v-else-if="data?.elements"
    ref="formRef"
    :name="data.id"
    class="space-y-4 rounded-xl bg-blue-lighter px-6 py-4 lg:rounded-2xl lg:p-9"
    @submit.prevent="onSubmit"
  >
    <h4 class="mb-10 font-serif text-xl text-black lg:text-2xl">{{ data?.title }}</h4>
    <ClientOnly>
      <div v-if="data.status === FORM_STATUS.CLOSED">
        <p v-if="data.form_close_message" class="text-pink" v-html="data.form_close_message"></p>
        <p v-else class="text-pink">{{ $t('form-close-message') }}</p>
      </div>

      <div v-if="data.status === FORM_STATUS.SCHEDULED && disabledForm">
        <p v-if="data.form_open_message" class="text-pink" v-html="data.form_open_message"></p>
        <p v-else class="text-pink">
          {{
            $t('form-open-message', {
              start: dayjs(data.open).format('DD-MM-YYYY'),
              end: dayjs(data.close).format('DD-MM-YYYY'),
            })
          }}
        </p>
      </div>
    </ClientOnly>
    <WebformElement
      v-for="item in data.elements"
      :key="item[0]"
      v-model="formFields[item[0]]"
      :iid="item[0]"
      :element="item[1]"
      :is-loading="isLoading"
      :form-id="data.id"
      :v$="v$"
      :disabled="disabledForm"
      :field_button_variant="data?.field_button_variant"
      :fields-from-lookup="ssnLookupFields"
    />
    <ClientOnly>
      <p v-if="error" class="px-1 py-0 pr-3 font-sans text-sm text-pink">
        {{ error }}
      </p>
    </ClientOnly>
  </form>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
dayjs.extend(isBetween)
import { useI18n } from '#imports'
import { ssnValidOrHasBirthDate, swedishMobile } from 'vuelidate-validators'
import { email, helpers } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import { useForm, useRecaptcha, flattenObj, FORM_STATUS, type FormType } from 'refresh-common'
import WebformElement from '@/components/molecules/WebformElement.vue'
import { extractFieldTitles } from '~/utils/form/getFieldTitles'

const config = useRuntimeConfig()
const { t } = useI18n()

interface LookupResponse {
  success: boolean
  fields: Record<string, boolean>
}

const props = defineProps<{ data: FormType }>()

const disabledForm = computed(() => {
  const { status, open, close } = props.data

  const startDate = open
  const endDate = close || dayjs().add(1, 'd')

  if (!status) {
    return false
  }

  if (status === FORM_STATUS.OPEN) {
    return false
  }

  if (status === FORM_STATUS.CLOSED) {
    return true
  }

  if (status === FORM_STATUS.SCHEDULED && dayjs().isBetween(startDate, endDate)) {
    return false
  }

  return true
})

const { isLoading, sendForm, updateDataForm, error } = useForm({
  cmsBasePath: config.public.cmsBasePath,
})
const { addRecatcha, recaptcha } = useRecaptcha({
  recaptchaSiteKey: config.public.recaptchaSiteKey,
  cmsBasePath: config.public.cmsBasePath,
})

const formFields = reactive<Record<string, any>>({})

const formRef = ref()
const showConfirmation = ref(false)
const confirmation = ref<HTMLElement | null>(null)

const ssnLookupFields = ref<Record<string, boolean>>({})

const stateValidation = reactive({
  agent_ssn: '',
  receiver_ssn: '',
  agent_email: '',
  agent_mobile: '',
})

const normalizeSSN = (ssn: string): string => {
  const digitsOnly = ssn.replace(/[-\s]/g, '')
  return digitsOnly.length === 12 ? digitsOnly.slice(2) : digitsOnly
}

updateDataForm(props.data.id, {})

const flatFormFields = computed(() => flattenObj(JSON.parse(JSON.stringify(formFields))))

// Debounce update to improve performance
const updateStateAndRecaptcha = useDebounceFn(() => {
  addRecatcha()

  const fields = flattenObj(JSON.parse(JSON.stringify(formFields)))

  const keysToValidate = ['agent_ssn', 'receiver_ssn', 'agent_email', 'agent_mobile']
  keysToValidate.forEach(key => {
    if (key in fields) {
      stateValidation[key as keyof typeof stateValidation] = fields[key]
    }
  })
}, 300)

watch(formFields, updateStateAndRecaptcha)

// Debounce lookup to improve performance
const lookupSsn = useDebounceFn(async (nesSSN: string) => {
  // Normalize the SSN
  const normalizedSSN = normalizeSSN(nesSSN)

  if (normalizedSSN?.length === 10) {
    try {
      const response = await $fetch<LookupResponse>('/api/contact/canLookup', {
        method: 'POST',
        body: { ssn: normalizedSSN },
      })
      if (response.success) {
        ssnLookupFields.value = response.fields
      } else {
        ssnLookupFields.value = {}
      }
    } catch (e) {
      console.error('SSN Lookup failed:', e)
      ssnLookupFields.value = {}
    }
  } else {
    ssnLookupFields.value = {}
  }
}, 300)

watch(
  () => flatFormFields.value.agent_ssn,
  nesSSN => {
    lookupSsn(nesSSN)
  },
)

const unwrapKeys = (inputArray: any[]) => {
  const resultsArray = inputArray.map((inputString: string) => {
    const startIndex = inputString.indexOf('[') + 1
    const endIndex = inputString.indexOf(']')

    if (startIndex !== -1 && endIndex !== -1) {
      return inputString.substring(startIndex, endIndex)
    } else {
      return ''
    }
  })
  return resultsArray
}

const hasBrackets = (arr: any[]) => {
  if (Array.isArray(arr) === false) {
    return false
  }
  const regex = /^[a-zA-Z0-9_]+\[[a-zA-Z0-9]+\]$/
  for (let str of arr) {
    if (regex.test(str)) {
      return true
    }
  }
  return false
}

const rules = computed(() => {
  let localRules: Record<string, any> = {}
  const fields = flattenObj(JSON.parse(JSON.stringify(formFields)))

  // Add field specific rules without overwriting the required rules
  if ('agent_ssn' in fields) {
    localRules = {
      ...localRules,
      agent_ssn: {
        ssn: helpers.withMessage(t('ssn-description'), ssnValidOrHasBirthDate),
      },
    }
  }

  if ('receiver_ssn' in fields) {
    localRules = {
      ...localRules,
      receiver_ssn: {
        ssn: helpers.withMessage(t('ssn-description'), ssnValidOrHasBirthDate),
      },
    }
  }

  if ('agent_email' in fields) {
    localRules = {
      ...localRules,
      agent_email: {
        email: helpers.withMessage(t('email.errorMessage'), email),
      },
    }
  }

  if ('agent_mobile' in fields) {
    localRules = {
      ...localRules,
      agent_mobile: {
        mobile: helpers.withMessage(t('mobile-number.swedish.error'), swedishMobile),
      },
    }
  }

  return localRules
})

const v$ = useVuelidate(rules, stateValidation)

const onSubmit = async () => {
  v$.value.$touch()

  if (v$.value.$invalid) return false

  const success = await recaptcha()

  if (!success) {
    return
  }

  // CheckboxesMulti values are wrapped in a certain way to make conditional fields work, here we check for that and unwrap them
  const data = flattenObj(JSON.parse(JSON.stringify(formFields)))
  const result: Record<string, any> = {}
  const original = Object.entries(data) as [string, any][]

  original.forEach(([key, value]) => {
    if (hasBrackets(value)) {
      result[key] = unwrapKeys(value)
    } else {
      result[key] = value
    }
  })

  const preferenceTitles = extractFieldTitles(props.data.elements, ['preferences_'])

  if ('agent_ssn' in result) {
    result.agent_ssn = normalizeSSN(result.agent_ssn)
  }
  if ('receiver_ssn' in result) {
    result.receiver_ssn = normalizeSSN(result.receiver_ssn)
  }

  const sent = await sendForm(props.data.id, { result, preferenceTitles })
  if (sent) {
    showConfirmation.value = true
    await nextTick()
    confirmation.value && confirmation.value.scrollIntoView({ behavior: 'smooth' })
  }
}
</script>
